import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { gestionarMovimiento, obtenerUrlArchivo } from '../servicios/api';

const ModalCrearGasto = ({ isOpen, onClose, onGastoCreado, gastoAEditar }) => {
  const [fecha, setFecha] = useState('');
  const [proveedor, setProveedor] = useState('');
  const [categoria, setCategoria] = useState('');
  const [responsable, setResponsable] = useState('');
  const [concepto, setConcepto] = useState('');
  const [monto, setMonto] = useState('');
  const [justificante, setJustificante] = useState(null);
  const [justificanteExistente, setJustificanteExistente] = useState(null); // Nuevo estado
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) { // Solo inicializar/resetear cuando el modal se abre
      if (gastoAEditar) {
        setFecha(gastoAEditar.fecha);
        setProveedor(gastoAEditar.proveedor || '');
        setCategoria(gastoAEditar.categoria || '');
        setResponsable(gastoAEditar.responsable || '');
        setConcepto(gastoAEditar.concepto || '');
        setMonto(gastoAEditar.monto.toString());
        setJustificante(null); // Resetear el input de archivo
        setJustificanteExistente(gastoAEditar.justificante || null);
      } else {
        // Resetear formulario para nueva creación
        setFecha(new Date().toISOString().slice(0, 10)); // Fecha actual por defecto
        setProveedor('');
        setCategoria('');
        setResponsable('');
        setConcepto('');
        setMonto('');
        setJustificante(null);
        setJustificanteExistente(null);
      }
      setError(null); // Limpiar errores al abrir
    }
  }, [isOpen, gastoAEditar]);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    const formData = new FormData();
    formData.append('tipo_movimiento', 'gasto');
    formData.append('fecha', fecha);
    formData.append('proveedor', proveedor);
    formData.append('categoria', categoria);
    formData.append('concepto', concepto);
    formData.append('monto', monto);
    formData.append('responsable', responsable);

    if (gastoAEditar) {
      formData.append('id_movimiento', gastoAEditar.id_movimiento); // Añadir ID para actualización
      if (justificante) {
        formData.append('justificante', justificante);
      } else if (justificanteExistente) {
        formData.append('justificante_existente', justificanteExistente); // Mantener justificante existente
      }
    } else {
      if (justificante) {
        formData.append('justificante', justificante);
      }
    }

    try {
      const response = await gestionarMovimiento(formData); // Usar gestionarMovimiento
      if (response.exito) {
        alert(response.mensaje);
        if (onGastoCreado) {
          onGastoCreado();
        }
        onClose();
      } else {
        setError(response.mensaje || 'Error al gestionar el gasto.');
      }
    } catch (err) {
      setError(err.response?.data?.mensaje || 'Error de conexión con el servidor. Inténtalo de nuevo más tarde.');
      console.error('Error al gestionar gasto:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50">
      <div className="bg-gray-900 p-6 rounded-lg shadow-lg w-full max-w-sm text-white max-h-screen overflow-y-auto">
        <h2 className="text-xl font-bold mb-4 text-red-500">{gastoAEditar ? 'Editar Gasto' : 'Añadir Nuevo Gasto'}</h2>
        {error && <div className="bg-red-500/20 p-3 rounded-lg text-red-300 mb-4">{error}</div>}
        <form onSubmit={handleSubmit}>
          <div className="mb-3">
            <label htmlFor="fecha" className="block text-sm font-bold mb-1">Fecha</label>
            <input 
              type="date" 
              id="fecha" 
              className="shadow appearance-none border border-gray-700 rounded w-full py-1.5 px-3 text-sm bg-gray-800 text-white leading-tight focus:outline-none focus:shadow-outline focus:ring-1 focus:ring-red-500" 
              value={fecha}
              onChange={(e) => setFecha(e.target.value)}
              required
            />
          </div>
          <div className="mb-3">
            <label htmlFor="proveedor" className="block text-sm font-bold mb-1">Proveedor</label>
            <input 
              type="text" 
              id="proveedor" 
              className="shadow appearance-none border border-gray-700 rounded w-full py-1.5 px-3 text-sm bg-gray-800 text-white leading-tight focus:outline-none focus:shadow-outline focus:ring-1 focus:ring-red-500" 
              value={proveedor}
              onChange={(e) => setProveedor(e.target.value)}
              required
            />
          </div>
          <div className="mb-3">
            <label htmlFor="categoria" className="block text-sm font-bold mb-1">Categoría</label>
            <input 
              type="text" 
              id="categoria" 
              className="shadow appearance-none border border-gray-700 rounded w-full py-1.5 px-3 text-sm bg-gray-800 text-white leading-tight focus:outline-none focus:shadow-outline focus:ring-1 focus:ring-red-500" 
              value={categoria}
              onChange={(e) => setCategoria(e.target.value)}
              list="categoria-sugerencias" 
              required
            />
            <datalist id="categoria-sugerencias">
              <option value="Materiales"></option>
              <option value="Combustible"></option>
              <option value="Alquiler"></option>
              <option value="Herramientas"></option>
              <option value="Oficina"></option>
              <option value="Servicios"></option>
              <option value="Salarios"></option>
              <option value="Transporte"></option>
              <option value="Marketing"></option>
              <option value="Otros"></option>
            </datalist>
          </div>
          <div className="mb-3">
            <label htmlFor="concepto" className="block text-sm font-bold mb-1">Concepto</label>
            <input 
              type="text" 
              id="concepto" 
              className="shadow appearance-none border border-gray-700 rounded w-full py-1.5 px-3 text-sm bg-gray-800 text-white leading-tight focus:outline-none focus:shadow-outline focus:ring-1 focus:ring-red-500" 
              value={concepto}
              onChange={(e) => setConcepto(e.target.value)}
              required
            />
          </div>
          <div className="mb-3">
            <label htmlFor="monto" className="block text-sm font-bold mb-1">Monto</label>
            <input 
              type="number" 
              id="monto" 
              step="0.01" 
              className="shadow appearance-none border border-gray-700 rounded w-full py-1.5 px-3 text-sm bg-gray-800 text-white leading-tight focus:outline-none focus:shadow-outline focus:ring-1 focus:ring-red-500" 
              value={monto}
              onChange={(e) => setMonto(e.target.value)}
              required
            />
          </div>
          <div className="mb-3">
            <label htmlFor="responsable" className="block text-sm font-bold mb-1">Responsable</label>
            <input 
              type="text" 
              id="responsable" 
              className="shadow appearance-none border border-gray-700 rounded w-full py-1.5 px-3 text-sm bg-gray-800 text-white leading-tight focus:outline-none focus:shadow-outline focus:ring-1 focus:ring-red-500" 
              value={responsable}
              onChange={(e) => setResponsable(e.target.value)}
              required
            />
          </div>
          <div className="mb-3">
            <label htmlFor="justificante" className="block text-sm font-bold mb-1">Justificante (Opcional)</label>
            <input 
              type="file" 
              id="justificante" 
              className="shadow appearance-none border border-gray-700 rounded w-full py-1.5 px-3 text-sm bg-gray-800 text-white leading-tight focus:outline-none focus:shadow-outline focus:ring-1 focus:ring-red-500 file:mr-4 file:py-1 file:px-2 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-red-50 file:text-red-700 hover:file:bg-red-100"
              onChange={(e) => setJustificante(e.target.files[0])}
            />
            {justificanteExistente && !justificante && (
              <p className="text-xs text-gray-400 mt-1">Archivo actual: <a href={obtenerUrlArchivo(`uploads/movimientos/${justificanteExistente}`)} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">{justificanteExistente}</a></p>
            )}
          </div>
          
          <div className="flex items-center justify-between mt-4">
            <button
              type="submit"
              className="bg-red-600 hover:bg-red-700 text-white font-bold py-1.5 px-4 text-sm rounded focus:outline-none focus:shadow-outline"
              disabled={loading}
            >
              {loading ? 'Guardando...' : (gastoAEditar ? 'Actualizar Gasto' : 'Guardar Gasto')}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="bg-gray-600 hover:bg-gray-700 text-white font-bold py-1.5 px-4 text-sm rounded focus:outline-none focus:shadow-outline"
              disabled={loading}
            >
              Cancelar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ModalCrearGasto;
